export type ProductNotificationPreferences = {
  visit_impressions: boolean;
  personal_offers: boolean;
  saved_place_updates: boolean;
};

export type ProfileSettingsData = {
  id: string;
  display_name: string;
  slug: string | null;
  bio: string | null;
  is_public: boolean;
  avatar_url: string | null;
  profile_path: string;
  notification_preferences: ProductNotificationPreferences;
};

export type ProfileSettingsResponse = {
  data: ProfileSettingsData;
};

export type SlugAvailabilityResponse = {
  slug: string;
  valid: boolean;
  available: boolean;
  message: string;
};

type ApiErrorBody = {
  message?: string;
  errors?: Record<string, string[]>;
};

export class ProfileSettingsApiError extends Error {
  readonly status: number;
  readonly errors: Record<string, string[]>;

  constructor(status: number, body: ApiErrorBody | null) {
    super(body?.message ?? `Profile settings request failed: ${status}`);
    this.name = 'ProfileSettingsApiError';
    this.status = status;
    this.errors = body?.errors ?? {};
  }
}

let csrfToken: string | null = null;

async function readBody(response: Response): Promise<unknown> {
  const type = response.headers.get('content-type') ?? '';
  return type.includes('application/json') ? response.json() : null;
}

async function csrf(): Promise<string> {
  if (csrfToken) return csrfToken;

  const response = await fetch('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const body = await readBody(response) as { csrf_token?: string } | null;

  if (!response.ok || !body?.csrf_token) {
    throw new ProfileSettingsApiError(response.status, body as ApiErrorBody | null);
  }

  csrfToken = body.csrf_token;
  return csrfToken;
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');

  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('X-CSRF-TOKEN', await csrf());
  }

  const response = await fetch(url, {
    ...options,
    method,
    credentials: 'same-origin',
    headers,
  });
  const body = await readBody(response);

  if (!response.ok) {
    throw new ProfileSettingsApiError(response.status, body as ApiErrorBody | null);
  }

  return body as T;
}

export function fetchProfileSettings(): Promise<ProfileSettingsResponse> {
  return request('/api/v1/me');
}

export function saveProfileSettings(payload: {
  display_name: string;
  slug: string | null;
  bio: string | null;
  is_public: boolean;
  notification_preferences: ProductNotificationPreferences;
}): Promise<ProfileSettingsResponse> {
  return request('/api/v1/me', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function checkSlugAvailability(slug: string, signal?: AbortSignal): Promise<SlugAvailabilityResponse> {
  const params = new URLSearchParams({ slug });
  return request(`/api/v1/me/slug-availability?${params.toString()}`, { signal });
}

export async function uploadProfileAvatar(file: File): Promise<{ mediaId: string; avatarUrl: string | null }> {
  const form = new FormData();
  form.append('photo', file);
  form.append('is_avatar', '1');
  form.append('visibility', 'public');

  const response = await request<{ data: { id: string; thumbnail_url?: string | null; url?: string | null } }>('/api/v1/media', {
    method: 'POST',
    body: form,
  });

  return {
    mediaId: response.data.id,
    avatarUrl: response.data.thumbnail_url ?? response.data.url ?? null,
  };
}
